import styled from '@emotion/styled';
import { useEffect, useState } from 'react';

import {
  Audio,
  AuthContainer,
  Button,
  Logo,
  PhoneField,
  PhoneFieldResult,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { tempLSPhoneKey, validatePhoneNumber } from '@taleemabad/shared';

interface SignInPageProps {
  logo: string;
  onSubmitted: (data: PhoneFieldResult) => void;
  showRegionChange: boolean;
  onRegionChange: () => void;
  isLoading?: boolean;
  onNeedHelp?: () => void;
}
export const SignInScreen = ({
  logo,
  onSubmitted,
  onRegionChange,
  showRegionChange,
  isLoading = false,
  onNeedHelp,
}: SignInPageProps) => {
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formData, setFormData] = useState<PhoneFieldResult>({
    hasError: !localStorage.getItem(tempLSPhoneKey),
    value: localStorage.getItem(tempLSPhoneKey) ?? '03',
    name: 'phone',
  });

  const handleSubmit = async () => {
    setHasSubmitted(true);
    onSubmitted(formData);
  };
  useEffect(() => {
    const phoneField = document.getElementById('phone') as HTMLInputElement;
    if (phoneField) {
      phoneField.focus();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [formData]);

  return (
    <AuthContainer>
      <StyledImageWrapper>
        <Logo
          altName="outer-layer"
          width={275}
          src={`../../assets/${logo}`}
          data-cy="sign-in-app-logo"
        />
      </StyledImageWrapper>

      <StyledCardContent>
        <Spacing marginBottom="md">
          <Typography
            variant="textHeadlineLargeSurface"
            data-cy="sign-in-get-started"
          >
            Get Started
          </Typography>
        </Spacing>
        <StyledDiv
          flexDirection="column"
          justifyContent="center"
          alignItem="flex-start"
          width="100%"
          marginBottom={12}
          marginTop={12}
        >
          <Audio
            isPlaying={true}
            src={
              'https://ai-textract-bucket.s3.ap-south-1.amazonaws.com/audio-files/Enter+phone+number.mp3'
            }
          />
        </StyledDiv>
        <Spacing marginBottom="xxl">
          <Typography
            variant="textBodyLargeSurface"
            data-cy="sign-in-phone-number-text"
          >
            Please enter your phone number
          </Typography>
        </Spacing>
        <PhoneField
          name="phone"
          type="tel"
          variant="outlined"
          label="Phone Number"
          placeholder="Enter here"
          value={formData.value}
          autocomplete="username"
          id="phone"
          data-cy="phone-number-field"
          errorText={hasSubmitted ? errorMessage : ''}
          onChange={(data: PhoneFieldResult): void => {
            data.hasError = !!validatePhoneNumber(data.value);
            setErrorMessage(validatePhoneNumber(data.value));
            setFormData(data);
          }}
        />
      </StyledCardContent>

      <Button
        children="Begin"
        type="submit"
        onClick={handleSubmit}
        data-cy="begin-button"
        loading={isLoading}
      />
      {onNeedHelp && (
        <StyledDiv marginTop={16} justifyContent="center">
          <HelpLinkWrapper onClick={onNeedHelp}>
            <Typography variant="textBodyMediumNeutral40" inline>
              Need Help
            </Typography>
            <Typography variant="textBodyMediumPrimary" inline>
              Signing In?
            </Typography>
          </HelpLinkWrapper>
        </StyledDiv>
      )}
      {showRegionChange && (
        <StyledDiv marginTop={16} justifyContent="center">
          <Button
            children="Change Region"
            onClick={onRegionChange}
            variant="text"
          />
        </StyledDiv>
      )}
    </AuthContainer>
  );
};

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 42px;
`;
const StyledImageWrapper = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  margin-bottom: 68px;
  ${mq(BreakpointSizes.xs)} {
    display: flex;
  }
`;

const HelpLinkWrapper = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 0;
  background: transparent;
  border: none;

  &:hover {
    opacity: 0.8;
  }
`;
